/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState, useEffect, useCallback } from "react";
import { Button, Dialog, AppBar, Typography } from "@material-ui/core";
import { syncUpdatedUsers, syncDeletedUsers } from "../../services/UserService";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../../utility/errorCodes";

const COMPONENT_NAME = "Admin SettingsManager/components/SyncUsersDialogue";

const USER_PROCESS_STATES = {
	INITIAL: "initial",
	PROCESS_NEW_USERS: "processNewUsers",
	DELETE_OLD_USERS: "deleteOldUsers",
	SHOW_RESULTS: "showResults",
	FINISHED: "finished",
};

/**
 * This is the component used to process new users as well as delete old users.
 * It displays the progress as well as a summary of results
 * @param {Object} usersToProcess An object containing the new users to update.
 * @param {(newUsers, deletedUsers) => void} finishSyncing A callback method to be called when syncing is finished.
 */
export const SyncUsersDialogue = ({ usersToProcess, finishSyncing }) => {
	const [processUsersTimeoutId, setProcessUsersTimeoutId] = useState(undefined);
	const [abortProcessUsers, setAbortProcessUsers] = useState(false);
	const [updatedUsers, setUpdatedUsersObject] = useState({});
	const [deletedUsers, setDeletedUsersList] = useState({});
	const [processingSteps, setProcessingSteps] = useState([]);
	const [currentProcessingStep, setCurrentProcessingStep] = useState(0);
	const [totalNewUsersToProcess, setTotalNewUsersToProcess] = useState(0);
	const [totalOldUsersToProcess, setTotalOldUsersToProcess] = useState(0);
	const [errors, setErrors] = useState({});
	React.useEffect(() => {
		setProcessingSteps([USER_PROCESS_STATES.INITIAL]);
		setTotalNewUsersToProcess(
			(usersToProcess?.newUsers?.newConnectUsers?.length || 0) +
				Object.keys(usersToProcess?.newUsers?.newCognitoUsers || {}).length,
		);
		setTotalOldUsersToProcess(
			(usersToProcess?.oldUsers?.oldConnectUsers?.length || 0) +
				Object.keys(usersToProcess?.oldUsers?.oldCognitoUsers || {}).length,
		);
		setCurrentProcessingStep(0);
	}, [usersToProcess]);

	const processOldUsers = useCallback(async (oldUsersToProcess, currentStep) => {
		try {
			if (oldUsersToProcess.oldConnectUsers) {
				const { updatedUsers, failedUsers } = await syncDeletedUsers({
					oldConnectUsers: oldUsersToProcess.oldConnectUsers,
				});
				if (updatedUsers) setDeletedUsersList(updatedUsers);
				if (failedUsers)
					setErrors((e) => ({
						...e,
						undeletedUsers: [...(e.undeletedUsers || []), ...failedUsers],
					}));
			}
		} catch (error) {
			v2.error(`Error while deleting users.`, {
				additionalData: { error: errorToObject(error) },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.DELETE_USERS_ERROR,
			});
			setErrors((e) => ({ ...e, deleteError: "An error occurred while attempting to delete the users." }));
			setDeletedUsersList(undefined);
		} finally {
			setCurrentProcessingStep(currentStep + 1);
		}
	}, []);

	const processNewUsers = useCallback(async (newUsersToProcess, currentStep) => {
		try {
			setProcessUsersTimeoutId(undefined);
			const userUpdateCalls = [];

			if (newUsersToProcess.newCognitoUsers) {
				userUpdateCalls.push(
					async () =>
						await syncUpdatedUsers({
							newCognitoUsers: newUsersToProcess.newCognitoUsers,
						}),
				);
			}
			if (newUsersToProcess.newConnectUsers) {
				let currentCallUsers = [];
				newUsersToProcess.newConnectUsers.forEach((user) => {
					currentCallUsers.push(user);
					/**
					 * Due to Amazon Connects API limitations we have to limit the amount of users we sync at once.
					 * Here we are limiting the users per API call and further down we have a timeout set for the api calls to occur
					 * every half second so as to not overwhelm the API limits.
					 */
					if (currentCallUsers.length > 1) {
						const newUsers = [...currentCallUsers];
						userUpdateCalls.push(
							async () =>
								await syncUpdatedUsers({
									newConnectUsers: [...newUsers],
								}),
						);
						currentCallUsers = [];
					}
				});
				if (currentCallUsers.length > 0) {
					userUpdateCalls.push(
						async () =>
							await syncUpdatedUsers({
								newConnectUsers: [...currentCallUsers],
							}),
					);
				}
			}
			const createNewUserTimeout = async (createUserCalls, currentStep, currentUsers = {}) => {
				if (createUserCalls.length > 0) {
					const { updatedUsers, failedUsers } = await createUserCalls.pop()();
					const newCurrentUsers = { ...currentUsers };
					if (updatedUsers)
						Object.keys(updatedUsers).forEach(
							(key) => (newCurrentUsers[key] = { ...newCurrentUsers[key], ...updatedUsers[key], newUser: true }),
						);
					if (failedUsers) {
						setErrors((e) => ({
							...e,
							unsyncedUsers: [...(e.unsyncedUsers || []), ...failedUsers],
						}));
					}
					setUpdatedUsersObject(newCurrentUsers);
					setProcessUsersTimeoutId(
						setTimeout(() => createNewUserTimeout(createUserCalls, currentStep, newCurrentUsers), 500),
					);
				} else {
					setCurrentProcessingStep(currentStep + 1);
					setProcessUsersTimeoutId(undefined);
				}
			};
			await createNewUserTimeout([...userUpdateCalls], currentStep);
		} catch (error) {
			v2.error(`Error while creating users.`, {
				additionalData: { error: errorToObject(error) },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.CREATE_NEW_USERS_ERROR,
			});
			setErrors((e) => ({
				...e,
				processError:
					"An unrecoverable error occurred while attempting to sync users and some users may have not been synced. ",
			}));
			setAbortProcessUsers(true);
			setCurrentProcessingStep(currentStep + 1);
		}
	}, []);

	React.useEffect(() => {
		switch (processingSteps[currentProcessingStep]) {
			case USER_PROCESS_STATES.PROCESS_NEW_USERS: {
				processNewUsers(usersToProcess.newUsers, currentProcessingStep);
				break;
			}
			case USER_PROCESS_STATES.DELETE_OLD_USERS: {
				processOldUsers(usersToProcess.oldUsers, currentProcessingStep);
				break;
			}
			default: {
			}
		}
	}, [processNewUsers, processOldUsers, usersToProcess, processingSteps, currentProcessingStep]);

	useEffect(() => {
		if (abortProcessUsers && processUsersTimeoutId) {
			clearTimeout(processUsersTimeoutId);
		}
	}, [abortProcessUsers, processUsersTimeoutId]);

	const getUpdateSection = (progressCount, totalUsers) => {
		return (
			<>
				<div style={{ height: "125px" }}>
					<i style={{ margin: "20px" }} className="fas fa-spinner loading-spinner spin-animation" />
					<div>
						{progressCount} / {totalUsers}
					</div>
				</div>
				<div style={{ display: "flex", justifyContent: "space-between" }}>
					<div />
					<Button
						variant="outlined"
						style={{
							opacity: 1,
							color: "#cc1f20",
							margin: "20px",
						}}
						onClick={() => {
							setAbortProcessUsers(true);
							setCurrentProcessingStep(currentProcessingStep + 1);
						}}
					>
						Skip
					</Button>
				</div>
			</>
		);
	};

	const getDisplayUserList = (userList) => {
		return (
			<>
				<div style={{ textAlign: "left" }}>
					<div style={{ display: "flex", marginTop: "10px" }}>
						<div style={{ width: "50%", marginLeft: "10px", fontWeight: "bold" }}>User Emails</div>
						<div style={{ width: "50%", fontWeight: "bold" }}>Connect Usernames</div>
					</div>
					<div
						style={{
							maxHeight: "300px",
							overflowY: "auto",
							overflowX: "clip",
							margin: "10px",
							textOverflow: "ellipsis",
							outline: "lightgray solid 1px",
						}}
					>
						{Object.values(userList).map((user) => {
							return (
								<div style={{ margin: "5px 5px", display: "flex" }} key={user.userId}>
									<div style={{ textOverflow: "ellipsis", overflow: "hidden", width: "50%" }}>
										{user.userId || "Not Found"}
									</div>
									<div
										style={{
											textOverflow: "ellipsis",
											overflow: "hidden",
											width: "50%",
											paddingLeft: "10px",
										}}
									>
										{user.username || "Not Found"}
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</>
		);
	};
	return (
		<Dialog
			open={usersToProcess !== undefined}
			onClose={() => {
				setProcessUsersTimeoutId(undefined);
				setAbortProcessUsers(false);
			}}
		>
			<AppBar color="default" style={{ position: "relative" }}>
				<Typography variant="h6" style={{ textAlign: "center", margin: "10px 25px 10px 25px" }}>
					{processingSteps[currentProcessingStep] === USER_PROCESS_STATES.INITIAL && "User Updates Found"}
					{processingSteps[currentProcessingStep] === USER_PROCESS_STATES.PROCESS_NEW_USERS && "Syncing Users To PACE"}
					{processingSteps[currentProcessingStep] === USER_PROCESS_STATES.DELETE_OLD_USERS &&
						"Removing Old Users From PACE"}
					{processingSteps[currentProcessingStep] === USER_PROCESS_STATES.SHOW_RESULTS && "Update Summary"}
				</Typography>
			</AppBar>
			<div style={{ minHeight: "200px", minWidth: "400px", margin: "auto 0px", textAlign: "center" }}>
				{processingSteps[currentProcessingStep] === USER_PROCESS_STATES.PROCESS_NEW_USERS && (
					<>
						<h6 style={{ margin: "10px" }}>Syncing users from Amazon Connect to PACE</h6>
						{getUpdateSection(
							Object.keys(updatedUsers || {}).length + (errors.unsyncedUsers || []).length,
							totalNewUsersToProcess,
						)}
					</>
				)}
				{processingSteps[currentProcessingStep] === USER_PROCESS_STATES.DELETE_OLD_USERS && (
					<>
						<h6 style={{ margin: "10px" }}>Removing PACE users that no longer exist in Amazon Connect.</h6>
						{getUpdateSection(Object.keys(deletedUsers || {}).length, totalOldUsersToProcess)}
					</>
				)}
				{processingSteps[currentProcessingStep] === USER_PROCESS_STATES.INITIAL && (
					<>
						<div style={{ height: "75px", margin: "10px" }}>
							{usersToProcess.newUsers?.newConnectUsers && (
								<>
									<div
										style={{ marginTop: "20px" }}
									>{`${usersToProcess.newUsers?.newConnectUsers?.length} new users found in Amazon Connect.`}</div>
								</>
							)}
							{usersToProcess.newUsers?.newCognitoUsers && (
								<h6>{`${usersToProcess.newUsers?.newCognitoUsers?.length} users to be synced from Cognito To PACE.`}</h6>
							)}
							{usersToProcess.oldUsers?.oldConnectUsers && (
								<>
									<div
										style={{ marginTop: "20px" }}
									>{`${usersToProcess.oldUsers?.oldConnectUsers?.length} users no longer found in Amazon Connect.`}</div>
								</>
							)}
						</div>
						<h6 style={{ margin: "25px 0px 20px 0px", textAlign: "center" }}>
							Would you like to sync these updates to PACE?
						</h6>
						<div style={{ display: "flex", justifyContent: "space-between", margin: "0px 20px 20px 20px" }}>
							<Button
								variant="outlined"
								onClick={() => {
									finishSyncing(undefined);
								}}
							>
								No
							</Button>
							<Button
								variant="outlined"
								onClick={() => {
									const newProcessingStepList = [USER_PROCESS_STATES.INITIAL];
									if (usersToProcess.newUsers.newConnectUsers || usersToProcess.newUsers.newCognitoUsers) {
										newProcessingStepList.push(USER_PROCESS_STATES.PROCESS_NEW_USERS);
									}
									if (usersToProcess.oldUsers.oldConnectUsers) {
										newProcessingStepList.push(USER_PROCESS_STATES.DELETE_OLD_USERS);
									}
									newProcessingStepList.push(USER_PROCESS_STATES.SHOW_RESULTS);
									setCurrentProcessingStep(1);
									setProcessingSteps(newProcessingStepList);
								}}
							>
								Yes
							</Button>
						</div>
					</>
				)}
				{processingSteps[currentProcessingStep] === USER_PROCESS_STATES.SHOW_RESULTS && (
					<div>
						<div style={{ height: "500px", minWidth: "500px", overflow: "auto", width: "100%", padding: "10px" }}>
							{errors && Object.keys(errors).length > 0 && (
								<>
									<h5>Errors</h5>
									{Object.keys(errors).map((errorKey) => {
										if (errorKey === "unsyncedUsers") {
											return (
												<>
													<div>The following {errors[errorKey].length} users failed to sync from Amazon Connect:</div>
													{getDisplayUserList(errors[errorKey])}
												</>
											);
										} else if (errorKey === "undeletedUsers") {
											return (
												<>
													<div>
														The following {Object.values(errors[errorKey]).length} users failed to be removed from PACE:
													</div>
													{getDisplayUserList(errors[errorKey])}
												</>
											);
										} else {
											return <div>{errors[errorKey]}</div>;
										}
									})}
								</>
							)}
							{updatedUsers && Object.keys(updatedUsers).length > 0 && (
								<>
									<h5>User Updates</h5>
									<div>{`${Object.keys(updatedUsers).length} users added or updated in PACE.`}</div>
									{getDisplayUserList(updatedUsers)}
								</>
							)}
							{deletedUsers && Object.keys(deletedUsers).length > 0 && (
								<>
									<h5>Delete Updates</h5>
									<div>{`${Object.keys(deletedUsers).length} users were removed from PACE.`}</div>
									{getDisplayUserList(deletedUsers)}
								</>
							)}
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								borderTop: "1px solid lightgray",
								padding: "10px 20px",
							}}
						>
							<div />
							<Button
								variant="outlined"
								onClick={() => {
									finishSyncing(updatedUsers, deletedUsers);
									setErrors({});
								}}
							>
								Ok
							</Button>
						</div>
					</div>
				)}
			</div>
		</Dialog>
	);
};
